import React from 'react';
import { Calendar, Clock, AlertTriangle } from 'lucide-react';

const UpcomingDeadlines = ({ accelerators }) => {
  // Get current date
  const today = new Date();
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(today.getMonth() + 3);

  // Filter accelerators with deadlines in the next 3 months
  const upcomingDeadlines = accelerators
    .filter(accelerator => {
      if (!accelerator.applicationDeadline) return false;
      
      const deadline = new Date(accelerator.applicationDeadline);
      return deadline >= today && deadline <= threeMonthsFromNow;
    })
    .sort((a, b) => new Date(a.applicationDeadline) - new Date(b.applicationDeadline));

  // Group by urgency (this week, this month, next 2 months)
  const getUrgency = (deadline) => {
    const daysUntil = Math.ceil((new Date(deadline) - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 7) return 'critical';
    if (daysUntil <= 30) return 'urgent';
    return 'upcoming';
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical': return <AlertTriangle size={16} />;
      case 'urgent': return <Clock size={16} />;
      case 'upcoming': return <Calendar size={16} />;
      default: return <Calendar size={16} />;
    }
  };

  const formatDeadline = (deadline) => {
    const date = new Date(deadline);
    const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil === 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil <= 7) return `${daysUntil} days`;
    if (daysUntil <= 30) return `${Math.ceil(daysUntil / 7)} weeks`;
    return `${Math.ceil(daysUntil / 30)} months`;
  };

  if (upcomingDeadlines.length === 0) {
    return (
      <div className="bg-white/90 rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Upcoming Deadlines</h3>
            <p className="text-sm text-gray-600">Next 3 months</p>
          </div>
        </div>
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No upcoming deadlines in the next 3 months</p>
          <p className="text-gray-400 text-xs mt-1">Add accelerators with application deadlines to see them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/90 rounded-2xl shadow-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Upcoming Deadlines</h3>
          <p className="text-sm text-gray-600">Next 3 months • {upcomingDeadlines.length} deadline{upcomingDeadlines.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="space-y-3">
        {upcomingDeadlines.map((accelerator) => {
          const urgency = getUrgency(accelerator.applicationDeadline);
          const urgencyColor = getUrgencyColor(urgency);
          const urgencyIcon = getUrgencyIcon(urgency);
          
          return (
            <div
              key={accelerator.id}
              className={`flex items-center justify-between p-4 rounded-xl border ${urgencyColor} hover:shadow-md transition-all cursor-pointer`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {urgencyIcon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm truncate">{accelerator.name}</h4>
                    {accelerator.tier && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        accelerator.tier === 'A' ? 'bg-red-100 text-red-800' :
                        accelerator.tier === 'B' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        Tier {accelerator.tier}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs mt-1">
                    <span className="text-gray-600">{accelerator.type}</span>
                    {accelerator.investment && (
                      <span className="text-gray-600">{accelerator.investment}</span>
                    )}
                    {accelerator.equity && (
                      <span className="text-gray-600">{accelerator.equity} equity</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-semibold">
                    {formatDeadline(accelerator.applicationDeadline)}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(accelerator.applicationDeadline).toLocaleDateString()}
                  </div>
                </div>
                <div className={`w-3 h-3 rounded-full ${
                  urgency === 'critical' ? 'bg-red-500' :
                  urgency === 'urgent' ? 'bg-orange-500' :
                  'bg-blue-500'
                }`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-gray-600">
                {upcomingDeadlines.filter(a => getUrgency(a.applicationDeadline) === 'critical').length} critical
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-gray-600">
                {upcomingDeadlines.filter(a => getUrgency(a.applicationDeadline) === 'urgent').length} urgent
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-gray-600">
                {upcomingDeadlines.filter(a => getUrgency(a.applicationDeadline) === 'upcoming').length} upcoming
              </span>
            </div>
          </div>
          <div className="text-gray-500 text-xs">
            Next deadline: {upcomingDeadlines[0] && formatDeadline(upcomingDeadlines[0].applicationDeadline)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingDeadlines; 